/**
 * Workshop Scheduler Utility
 * Automatically calculates and updates workshop timing for the upcoming week
 * Updates every Sunday at 12:00 AM
 */

// Workshop configuration
const WORKSHOP_CONFIG = {
  // Workshop runs every Sunday from 10:00 AM to 1:00 PM IST
  dayOfWeek: 0, // Sunday = 0
  startHour: 10,
  startMinute: 0,
  endHour: 13,
  endMinute: 0,
  timezone: 'Asia/Kolkata' // IST timezone
};

/**
 * Get the next Sunday's date
 * @returns {Date} Next Sunday's date
 */
export function getNextSunday() {
  const today = new Date();
  const currentDay = today.getDay();
  
  // If today is Sunday and it's before workshop time, return today
  // Otherwise, return next Sunday
  if (currentDay === 0) {
    const currentHour = today.getHours();
    const currentMinute = today.getMinutes();
    const workshopStartTime = WORKSHOP_CONFIG.startHour * 60 + WORKSHOP_CONFIG.startMinute;
    const currentTime = currentHour * 60 + currentMinute;
    
    if (currentTime < workshopStartTime) {
      return today;
    }
  }
  
  // Calculate days until next Sunday
  const daysUntilSunday = currentDay === 0 ? 7 : 7 - currentDay;
  const nextSunday = new Date(today.getTime() + daysUntilSunday * 24 * 60 * 60 * 1000);
  
  return nextSunday;
}

/**
 * Get the workshop date and time for the upcoming week
 * @returns {Object} Workshop timing details
 */
export function getUpcomingWorkshopTiming() {
  const nextSunday = getNextSunday();
  
  // Set the workshop time
  const workshopDate = new Date(nextSunday);
  workshopDate.setHours(WORKSHOP_CONFIG.startHour, WORKSHOP_CONFIG.startMinute, 0, 0);
  
  const workshopEndDate = new Date(nextSunday);
  workshopEndDate.setHours(WORKSHOP_CONFIG.endHour, WORKSHOP_CONFIG.endMinute, 0, 0);
  
  // Format date for display
  const options = { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    timeZone: WORKSHOP_CONFIG.timezone
  };
  
  const dateString = workshopDate.toLocaleDateString('en-IN', options);
  const timeString = `${WORKSHOP_CONFIG.startHour}:${WORKSHOP_CONFIG.startMinute.toString().padStart(2, '0')} AM - ${WORKSHOP_CONFIG.endHour}:${WORKSHOP_CONFIG.endMinute.toString().padStart(2, '0')} PM IST`;
  
  // Tamil date formatting
  const tamilMonths = {
    'January': 'ஜனவரி',
    'February': 'பிப்ரவரி', 
    'March': 'மார்ச்',
    'April': 'ஏப்ரல்',
    'May': 'மே',
    'June': 'ஜூன்',
    'July': 'ஜூலை',
    'August': 'ஆகஸ்ட்',
    'September': 'செப்டம்பர்',
    'October': 'அக்டோபர்',
    'November': 'நவம்பர்',
    'December': 'டிசம்பர்'
  };
  
  const tamilDays = {
    'Sunday': 'ஞாயிறு',
    'Monday': 'திங்கள்',
    'Tuesday': 'செவ்வாய்',
    'Wednesday': 'புதன்',
    'Thursday': 'வியாழன்',
    'Friday': 'வெள்ளி',
    'Saturday': 'சனி'
  };
  
  const englishMonth = workshopDate.toLocaleDateString('en-US', { month: 'long' });
  const englishDay = workshopDate.toLocaleDateString('en-US', { weekday: 'long' });
  
  const tamilDateString = `${tamilDays[englishDay]}, ${workshopDate.getDate()} ${tamilMonths[englishMonth]} ${workshopDate.getFullYear()}`;
  
  return {
    date: workshopDate,
    endDate: workshopEndDate,
    dateString,
    timeString,
    tamilDateString,
    isToday: isToday(workshopDate),
    daysUntil: Math.ceil((workshopDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)),
    formattedForDisplay: {
      english: `${dateString} | ${timeString}`,
      tamil: `${tamilDateString} | காலை 10:00 - மதியம் 1:00 IST`,
      short: `${englishDay}, ${workshopDate.getDate()} ${englishMonth} | 10:00 AM - 1:00 PM IST`
    }
  };
}

/**
 * Check if a date is today
 * @param {Date} date 
 * @returns {boolean}
 */
function isToday(date) {
  const today = new Date();
  return date.getDate() === today.getDate() &&
         date.getMonth() === today.getMonth() &&
         date.getFullYear() === today.getFullYear();
}

/**
 * Get countdown timer data for workshop
 * @returns {Object} Countdown data
 */
export function getWorkshopCountdown() {
  const workshop = getUpcomingWorkshopTiming();
  const now = new Date();
  const difference = workshop.date.getTime() - now.getTime();
  
  if (difference <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      isExpired: true
    };
  }
  
  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);
  
  return {
    days,
    hours,
    minutes,
    seconds,
    isExpired: false,
    totalMilliseconds: difference
  };
}

/**
 * Check if it's time to update the workshop schedule (Sunday 12:00 AM)
 * @returns {boolean}
 */
export function shouldUpdateSchedule() {
  const now = new Date();
  const isSunday = now.getDay() === 0;
  const isMidnight = now.getHours() === 0 && now.getMinutes() === 0;
  
  return isSunday && isMidnight;
}

/**
 * Get motivational message based on time until workshop
 * @returns {string}
 */
export function getMotivationalMessage() {
  const workshop = getUpcomingWorkshopTiming();
  const daysUntil = workshop.daysUntil;
  
  if (daysUntil === 0) {
    return "🔥 Today is the day! Workshop starts in a few hours!";
  } else if (daysUntil === 1) {
    return "⚡ Tomorrow is your transformation day! Get ready!";
  } else if (daysUntil <= 3) {
    return `🚀 Just ${daysUntil} days left! Your AI journey begins soon!`;
  } else if (daysUntil <= 7) {
    return `💪 ${daysUntil} days to go! Time to prepare for your breakthrough!`;
  } else {
    return `📅 Mark your calendar! ${daysUntil} days until your AI transformation!`;
  }
}

/**
 * Storage key for workshop timing cache
 */
const STORAGE_KEY = 'workshop_timing_cache';

/**
 * Cache workshop timing data
 * @param {Object} timingData 
 */
export function cacheWorkshopTiming(timingData) {
  try {
    const cacheData = {
      timing: timingData,
      cachedAt: new Date().toISOString(),
      expiresAt: getNextUpdateTime().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Failed to cache workshop timing:', error);
  }
}

/**
 * Get cached workshop timing data
 * @returns {Object|null}
 */
export function getCachedWorkshopTiming() {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (!cached) return null;
    
    const cacheData = JSON.parse(cached);
    const expiresAt = new Date(cacheData.expiresAt);
    
    // Check if cache is still valid
    if (new Date() > expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    
    return cacheData.timing;
  } catch (error) {
    console.warn('Failed to get cached workshop timing:', error);
    return null;
  }
}

/**
 * Get the next update time (next Sunday 12:00 AM)
 * @returns {Date}
 */
function getNextUpdateTime() {
  const now = new Date();
  const nextSunday = new Date(now);
  const daysUntilSunday = now.getDay() === 0 ? 7 : 7 - now.getDay();
  
  nextSunday.setDate(now.getDate() + daysUntilSunday);
  nextSunday.setHours(0, 0, 0, 0);
  
  return nextSunday;
}

/**
 * Format workshop timing for different contexts
 * @param {Object} timing 
 * @returns {Object}
 */
export function formatWorkshopTiming(timing) {
  return {
    heroSection: {
      primary: timing.formattedForDisplay.short,
      secondary: timing.formattedForDisplay.tamil,
      countdown: `Next Batch: ${timing.formattedForDisplay.short}`
    },
    modal: {
      title: `Workshop on ${timing.dateString}`,
      subtitle: `${timing.timeString} - Live in Tamil`,
      description: `Join us this ${timing.dateString.split(',')[0]} for an exclusive AI workshop`
    },
    cta: {
      urgent: timing.daysUntil <= 2 ? `Workshop ${timing.isToday ? 'TODAY' : 'TOMORROW'}!` : '',
      standard: `Next Workshop: ${timing.formattedForDisplay.short}`
    }
  };
}